// src/components/modals/TemplateLibraryModal.tsx
import React, { useState, useEffect } from 'react';

interface AgentTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  domain: string;
  defaultTestCategories: string[];
  sampleTestCases: {
    name: string;
    type: string;
    taskPrompt: string;
    expectedOutcome: string;
  }[];
  tags: string[];
}

interface TemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUseTemplate: (template: AgentTemplate) => void;
}

const TemplateLibraryModal: React.FC<TemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onUseTemplate
}) => {
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');

  // Load templates from localStorage
  useEffect(() => {
    const loadTemplates = () => {
      try {
        const saved = localStorage.getItem('agentTemplates');
        const savedTemplates = saved ? JSON.parse(saved) : [];

        // Add some default templates if none exist
        if (savedTemplates.length === 0) {
          const defaultTemplates: AgentTemplate[] = [
            {
              id: 'default_ecommerce',
              name: 'E-commerce Customer Support',
              description: 'Template for e-commerce customer service agents handling orders, returns, and inquiries',
              type: 'customer_service',
              domain: 'e-commerce',
              defaultTestCategories: ['Functional', 'Reliability', 'Guardrail'],
              sampleTestCases: [
                {
                  name: 'Order Status Inquiry',
                  type: 'Functional',
                  taskPrompt: 'Can you help me track my order? My order number is 12345',
                  expectedOutcome: 'Agent should ask for verification details and provide order status'
                },
                {
                  name: 'Return Request',
                  type: 'Functional',
                  taskPrompt: 'I want to return this item I bought last week',
                  expectedOutcome: 'Agent should guide through return process and policy'
                }
              ],
              tags: ['customer-service', 'orders', 'returns']
            },
            {
              id: 'default_healthcare',
              name: 'Healthcare Assistant',
              description: 'Template for healthcare assistants helping with appointments and basic inquiries',
              type: 'customer_service',
              domain: 'healthcare',
              defaultTestCategories: ['Functional', 'Compliance', 'Guardrail'],
              sampleTestCases: [
                {
                  name: 'Appointment Scheduling',
                  type: 'Functional',
                  taskPrompt: 'I need to schedule an appointment with Dr. Smith',
                  expectedOutcome: 'Agent should check availability and book appointment'
                }
              ],
              tags: ['healthcare', 'appointments', 'medical']
            },
            {
              id: 'default_finance',
              name: 'Financial Services Agent',
              description: 'Template for financial services customer support handling accounts and transactions',
              type: 'customer_service',
              domain: 'finance',
              defaultTestCategories: ['Functional', 'Compliance', 'Guardrail'],
              sampleTestCases: [
                {
                  name: 'Account Balance Inquiry',
                  type: 'Functional',
                  taskPrompt: 'What is my current account balance?',
                  expectedOutcome: 'Agent should verify identity and provide balance information'
                }
              ],
              tags: ['finance', 'banking', 'accounts']
            }
          ];

          localStorage.setItem('agentTemplates', JSON.stringify(defaultTemplates));
          setTemplates(defaultTemplates);
        } else {
          setTemplates(savedTemplates);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        setTemplates([]);
      }
    };

    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Filter templates based on search and filters
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchFilter.toLowerCase()));

    const matchesType = typeFilter === 'all' || template.type === typeFilter;
    const matchesDomain = domainFilter === 'all' || template.domain === domainFilter;

    return matchesSearch && matchesType && matchesDomain;
  });

  const handleUseTemplate = (template: AgentTemplate) => {
    onUseTemplate(template);
    onClose();
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      setTemplates(updatedTemplates);
      localStorage.setItem('agentTemplates', JSON.stringify(updatedTemplates));

      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
           style={{width: '95vw', maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto'}}>

        <div className="modal-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
          <h2>📚 Agent Template Library</h2>
          <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}>
            ✕
          </button>
        </div>

        {/* Search and Filters */}
        <div className="template-filters" style={{marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px'}}>
          <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
            <input
              type="text"
              placeholder="🔍 Search templates..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              style={{padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            >
              <option value="all">All Types</option>
              <option value="customer_service">Customer Service</option>
              <option value="data_processor">Data Processor</option>
              <option value="decision_maker">Decision Maker</option>
              <option value="content_creator">Content Creator</option>
              <option value="analyst">Analyst</option>
              <option value="chatbot">Chatbot</option>
            </select>

            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              style={{padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            >
              <option value="all">All Domains</option>
              <option value="e-commerce">E-commerce</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="education">Education</option>
              <option value="insurance">Insurance</option>
              <option value="real-estate">Real Estate</option>
              <option value="travel">Travel & Hospitality</option>
              <option value="technology">Technology</option>
              <option value="general">General Purpose</option>
            </select>
          </div>

          <div style={{fontSize: '0.9rem', color: '#666'}}>
            Showing {filteredTemplates.length} of {templates.length} templates
          </div>
        </div>

        <div style={{display: 'grid', gridTemplateColumns: selectedTemplate ? '1fr 1fr' : '1fr', gap: '1.5rem'}}>
          {/* Template List */}
          <div className="template-list">
            <h3 style={{marginBottom: '1rem'}}>Available Templates</h3>

            {filteredTemplates.length === 0 ? (
              <div style={{textAlign: 'center', padding: '2rem', color: '#666'}}>
                {templates.length === 0 ? (
                  <>
                    <p> No templates found.</p>
                    <p>Create your first template to get started!</p>
                  </>
                ) : (
                  <p>🔍 No templates match your search criteria.</p>
                )}
              </div>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '500px', overflow: 'auto'}}>
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTemplate(template)}
                    style={{
                      padding: '1rem',
                      border: selectedTemplate?.id === template.id ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: selectedTemplate?.id === template.id ? '#f0f8ff' : '#fff',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem'}}>
                      <h4 style={{margin: 0, color: '#333'}}>{template.name}</h4>
                      <div style={{display: 'flex', gap: '0.25rem'}}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUseTemplate(template);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Use
                        </button>
                        {!template.id.startsWith('default_') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template.id);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.75rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>

                    <p style={{margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#666', lineHeight: 1.4}}>
                      {template.description}
                    </p>

                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem'}}>
                      <div style={{color: '#666'}}>
                        {template.type.replace('_', ' ')} • {template.domain}
                      </div>
                      <div style={{display: 'flex', gap: '0.25rem'}}>
                        {template.tags.slice(0, 2).map(tag => (
                          <span key={tag} style={{
                            padding: '0.15rem 0.3rem',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '8px',
                            fontSize: '0.7rem'
                          }}>
                            {tag}
                          </span>
                        ))}
                        {template.tags.length > 2 && (
                          <span style={{color: '#666', fontSize: '0.7rem'}}>
                            +{template.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Template Details */}
          {selectedTemplate && (
            <div className="template-details">
              <h3 style={{marginBottom: '1rem'}}>Template Details</h3>

              <div style={{
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '1.5rem',
                backgroundColor: '#fff',
                maxHeight: '500px',
                overflow: 'auto'
              }}>
                <h4 style={{color: '#333', marginBottom: '1rem'}}>{selectedTemplate.name}</h4>

                <div style={{marginBottom: '1rem'}}>
                  <strong>Description:</strong>
                  <p style={{margin: '0.25rem 0', color: '#666'}}>{selectedTemplate.description}</p>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                  <div>
                    <strong>Type:</strong> {selectedTemplate.type.replace('_', ' ')}
                  </div>
                  <div>
                    <strong>Domain:</strong> {selectedTemplate.domain}
                  </div>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <strong>Default Test Categories:</strong>
                  <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap'}}>
                    {selectedTemplate.defaultTestCategories.map(category => (
                      <span key={category} style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#f0f8ff',
                        color: '#0066cc',
                        borderRadius: '12px',
                        fontSize: '0.8rem'
                      }}>
                        {category}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <strong>Tags:</strong>
                  <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap'}}>
                    {selectedTemplate.tags.map(tag => (
                      <span key={tag} style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        borderRadius: '12px',
                        fontSize: '0.8rem'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <strong>Sample Test Cases ({selectedTemplate.sampleTestCases.length}):</strong>
                  <div style={{marginTop: '0.5rem'}}>
                    {selectedTemplate.sampleTestCases.map((testCase, index) => (
                      <div key={index} style={{
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem'}}>
                          <strong style={{fontSize: '0.9rem'}}>{testCase.name}</strong>
                          <span style={{
                            padding: '0.15rem 0.3rem',
                            backgroundColor: '#fff',
                            color: '#666',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            border: '1px solid #ddd'
                          }}>
                            {testCase.type}
                          </span>
                        </div>
                        <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem'}}>
                          <strong>Prompt:</strong> {testCase.taskPrompt}
                        </div>
                        <div style={{fontSize: '0.8rem', color: '#666'}}>
                          <strong>Expected:</strong> {testCase.expectedOutcome}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Use Template Button */}
              <div style={{marginTop: '1rem'}}>
                <button
                  className="widget-button button-primary"
                  onClick={() => handleUseTemplate(selectedTemplate)}
                  style={{width: '100%'}}
                >
                  🚀 Use This Template
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateLibraryModal;